import React, {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import ModalShell from './ModalShell';
import tradingService from '../services/tradingService';
import {isTruthy} from '../utils/boolean';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';

  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 4,
  }).format(number);
};

const formatProfitFactor = (strategy) => {
  const performance = strategy?.performance ?? {};
  const rawValue = performance?.profit_factor;
  const value = Number(rawValue);

  if (performance?.profit_factor_is_infinite) {
    return '∞';
  }

  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return '—';
  }

  if (!Number.isFinite(value)) {
    return String(rawValue);
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDateTime = (value) => {
  if (!value) return 'No trades yet';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export default function StrategyCard({ strategy }) {
  const navigate = useNavigate();

  const nameText = strategy?.name ?? '—';
  const descText = strategy?.description ?? '—';
  const isActive = isTruthy(strategy?.is_active);
  const activeText = isActive ? 'Active' : 'Inactive';
  const isPublic = isTruthy(strategy?.is_public);
  const visibilityText = isPublic ? 'Public' : 'Private';

  const account = strategy?.account ?? {};
  const performance = strategy?.performance ?? {};
  const subscriptions = strategy?.subscriptions ?? {};

  const exchangeName = account?.exchange?.exchange_name ?? '—';
  const activeSubscribers = formatNumber(subscriptions?.active);
  const totalSubscribers = formatNumber(subscriptions?.total);
  const totalTrades = formatNumber(performance?.total_trades);
  const executedTrades = formatNumber(performance?.executed_trades);
  const profitFactor = formatProfitFactor(strategy);
  const lastTradeText = formatDateTime(performance?.last_trade_date);

  const detailsPath = strategy?.short_id ? `/strategies/${encodeURIComponent(strategy.short_id)}` : null;

  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [accounts] = useState([]);
  const [accountsLoading] = useState(false);

  const [accountNumber, setAccountNumber] = useState('');
  const [volumeLimit, setVolumeLimit] = useState('0.9');

  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalOk, setModalOk] = useState('');

  const openSubscribe = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!isActive) return;
    setSubscribeOpen(true);
  };

  const closeSubscribe = () => {
    if (submitting) return;
    setSubscribeOpen(false);
    setModalError('');
    setModalOk('');
  };
  const accountOptions = useMemo(() => {
    return (Array.isArray(accounts) ? accounts : [])
      .map((a) => {
        const number = String(a?.number ?? '').trim();
        if (!number) return null;
        const label = String(a?.label ?? '').trim();
        return {
          value: number,
          label: label || 'Account',
        };
      })
      .filter(Boolean);
  }, [accounts]);

  const submitSubscribe = async () => {
    setModalError('');
    setModalOk('');

    if (!isActive) {
      setModalError('Strategy is not active');
      return;
    }

    if (!strategy?.short_id) {
      setModalError('Strategy short_id is missing');
      return;
    }
    if (!accountNumber) {
      setModalError('Please select an account');
      return;
    }

    const vol = Number(volumeLimit);
    if (!Number.isFinite(vol) || vol <= 0) {
      setModalError('Volume limit must be a number greater than 0');
      return;
    }

    try {
      setSubmitting(true);

      await tradingService.accounts.strategy.subscribe({
        number: accountNumber,
        strategyShortId: strategy.short_id,
        volume_limit: vol,
      });

      setModalOk('Subscription created');
      setTimeout(() => setSubscribeOpen(false), 600);
    } catch (err) {
      setModalError(err?.message || 'Failed to create subscription');
      setSubmitting(false);
    }
  };

return (
    <>
      <article
        className="strategies-page__strategy-card"
        aria-label={`Strategy ${nameText}`}
        role="group"
      >
        <div className="strategies-page__strategy-card-top">
          <div className="strategies-page__strategy-meta">
            <div className="strategies-page__strategy-name" title={nameText}>
              {nameText}
            </div>
          </div>

          <span
            className={
              isActive
                ? 'strategies-page__status strategies-page__status--active'
                : 'strategies-page__status strategies-page__status--inactive'
            }
          >
            {activeText}
          </span>
          <span
            className={
              isPublic
                ? 'strategies-page__status strategies-page__status--public'
                : 'strategies-page__status strategies-page__status--private'
            }
          >
            {visibilityText}
          </span>
        </div>

        <div className="strategies-page__strategy-kv">
          <div className="strategies-page__kv">
            <div className="strategies-page__kv-k">Description</div>
            <div className="strategies-page__kv-v strategies-page__strategy-description" title={descText}>
              {descText}
            </div>
          </div>

          <div className="strategies-page__summary-grid">
            <div className="strategies-page__kv">
              <div className="strategies-page__kv-k">Exchange</div>
              <div className="strategies-page__kv-v" title={exchangeName}>
                {exchangeName}
              </div>
            </div>

            <div className="strategies-page__kv">
              <div className="strategies-page__kv-k">Subscribers</div>
              <div
                className="strategies-page__kv-v"
                title={`Active / Total: ${activeSubscribers} / ${totalSubscribers}`}
              >
                {activeSubscribers} active
              </div>
            </div>

            <div className="strategies-page__kv">
              <div className="strategies-page__kv-k">Trades</div>
              <div
                className="strategies-page__kv-v"
                title={`Total / Executed: ${totalTrades} / ${executedTrades}`}
              >
                {totalTrades} total · {executedTrades} executed
              </div>
            </div>

            <div className="strategies-page__kv">
              <div className="strategies-page__kv-k">Profit factor</div>
              <div className="strategies-page__kv-v" title="Gross profit divided by gross loss">
                {profitFactor}
              </div>
            </div>
          </div>

          <div className="strategies-page__kv">
            <div className="strategies-page__kv-k">Last trade</div>
            <div className="strategies-page__kv-v" title={lastTradeText}>
              {lastTradeText}
            </div>
          </div>
        </div>

        <div className="strategies-page__strategy-actions">
          <button
            type="button"
            className="strategies-page__btn strategies-page__btn--primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (detailsPath) navigate(detailsPath);
            }}
            disabled={!detailsPath}
            title={!detailsPath ? 'Strategy details are unavailable' : 'Open details'}
          >
            Details
          </button>

          {isActive ? (
            <button
              type="button"
              className="strategies-page__btn"
              onClick={openSubscribe}
              disabled={!strategy?.short_id}
              title={!strategy?.short_id ? 'Strategy short_id is missing' : 'Create subscription'}
            >
              Subscribe
            </button>
          ) : null}
        </div>
      </article>

      <ModalShell
        title={`Subscribe to ${nameText}`}
        open={subscribeOpen}
        onClose={closeSubscribe}
        footer={
          <>
            <button type="button" onClick={closeSubscribe} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              onClick={submitSubscribe}
              disabled={submitting || accountsLoading || !accountNumber}
            >
              {submitting ? 'Creating…' : 'Create subscription'}
            </button>
          </>
        }
      >
        {modalError ? <div style={{ color: '#fecaca', marginBottom: 10 }}>{modalError}</div> : null}
        {modalOk ? <div style={{ color: '#bbf7d0', marginBottom: 10 }}>{modalOk}</div> : null}

        <div style={{ display: 'grid', gap: 12 }}>
          <label>
            <div style={{ fontWeight: 650, marginBottom: 6 }}>Account</div>
            <select
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              disabled={accountsLoading || submitting}
              style={{ width: '100%' }}
            >
              <option value="" disabled>
                {accountsLoading ? 'Loading accounts…' : 'Select account…'}
              </option>
              {accountOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <div style={{ fontWeight: 650, marginBottom: 6 }}>Volume limit</div>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={volumeLimit}
              onChange={(e) => setVolumeLimit(e.target.value)}
              disabled={submitting}
              style={{ width: '100%' }}
            />
          </label>

        </div>
      </ModalShell>
    </>
  );
}
